import React, { useCallback, useEffect, useState } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { CenterLoader } from 'global/styles';
import { get, orderBy } from 'global/lib/lodash';
import { TextField, Switch } from '@mui/material';
import sendData from 'redux/actions/sendData';
import SnackAlerts from 'global/components/snack-alerts';
import Modal from 'global/components/modal';
import { TradesContainer } from './styles';
import { FlexGridDataTable, FlexGridListHint } from 'global/components/flex-grid-list';

export const TradesList = (props) => {
  const [alerts, setAlerts] = useState();
  const [editTrade, setEditTrade] = useState(null);
  const [draft, setDraft] = useState({ name: '', clientKey: '' });
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({
    data: [],
    sort: {
      name: 'asc',
      customName: 'asc',
      clientKey: 'asc',
    },
  });

  const nameSortDir = get(state, 'sort.name');
  const sortData = useCallback((d) => orderBy(d, 'defaultName', nameSortDir), [nameSortDir]);

  const trades = useRetrieveData(
    { url: '/trade-types' },
    {
      version: 'v2',
      build: ['tradeTypes'],
    }
  );

  useEffect(() => {
    setState((s) => ({ ...s, data: sortData(trades.data) }));
  }, [trades.data, sortData]);

  const patchTrade = async (trade, attributes) => {
    await sendData(
      {
        url: `/trade-types/${trade.id}`,
        method: 'patch',
        data: {
          data: {
            id: trade.id,
            type: 'trade-types',
            attributes,
          },
        },
      },
      { version: 'v2' }
    );
  };

  const onToggleEnabled = async (trade) => {
    const nextEnabled = !get(trade, 'enabled');
    setState((s) => ({
      ...s,
      data: s.data.map((d) => (d.id === trade.id ? { ...d, enabled: nextEnabled } : d)),
    }));
    try {
      await patchTrade(trade, { enabled: nextEnabled });
      trades.update();
    } catch (err) {
      console.error(err);
      setAlerts({ message: err.message, severity: 'error' });
      setState((s) => ({
        ...s,
        data: s.data.map((d) => (d.id === trade.id ? { ...d, enabled: !nextEnabled } : d)),
      }));
    }
  };

  const openEdit = (trade) => {
    setEditTrade(trade);
    setDraft({
      name: get(trade, 'name') ?? '',
      clientKey: get(trade, 'clientKey') ?? '',
    });
  };

  const closeEdit = () => {
    if (saving) return;
    setEditTrade(null);
  };

  const submitEdit = async () => {
    if (!editTrade) return;
    try {
      setSaving(true);
      await patchTrade(editTrade, {
        name: draft.name,
        client_key: draft.clientKey,
        enabled: !!get(editTrade, 'enabled'),
      });
      setState((s) => ({
        ...s,
        data: s.data.map((d) =>
          d.id === editTrade.id ? { ...d, name: draft.name, clientKey: draft.clientKey } : d
        ),
      }));
      setEditTrade(null);
      trades.update();
    } catch (err) {
      console.error(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const toggleSort = (field, dataKey) => {
    const dir = get(state, `sort.${field}`) === 'asc' ? 'desc' : 'asc';
    const ord = orderBy(state.data, dataKey, dir);
    setState((s) => ({ ...s, data: ord, sort: { ...s.sort, [field]: dir } }));
  };

  const columns = [
    {
      field: 'enabled',
      title: 'Enabled',
      width: '12%',
      minWidth: 40,
      cellSx: { mr: { xs: 1, sm: 0 }, overflow: 'visible' },
      render: (c) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Switch
            size="small"
            color="primary"
            checked={!!get(c, 'enabled')}
            onChange={() => onToggleEnabled(c)}
          />
        </span>
      ),
    },
    {
      field: 'name',
      title: 'Name',
      width: '28%',
      stackAt: 'sm',
      sortable: true,
      sortDir: state.sort.name,
      onSort: () => toggleSort('name', 'defaultName'),
      render: (c) => get(c, 'defaultName'),
    },
    {
      field: 'customName',
      title: 'Custom Name',
      width: '30%',
      hideBelow: 'sm',
      cellSx: { pr: 1.5 },
      sortable: true,
      sortDir: state.sort.customName,
      onSort: () => toggleSort('customName', 'name'),
      render: (c) => get(c, 'name'),
    },
    {
      field: 'clientKey',
      title: 'Key ID',
      width: '30%',
      hideBelow: 'md',
      cellSx: { pl: 0.5 },
      sortable: true,
      sortDir: state.sort.clientKey,
      onSort: () => toggleSort('clientKey', 'clientKey'),
      render: (c) => get(c, 'clientKey'),
    },
  ];

  if (trades.loading && !state.data.length) return <CenterLoader />;

  return (
    <div>
      <TradesContainer>
        <FlexGridListHint />
        <FlexGridDataTable
          items={state.data}
          columns={columns}
          onSelectRow={openEdit}
          getRowProps={(trade) => ({ dimmed: !get(trade, 'enabled') })}
        />
      </TradesContainer>

      <Modal
        open={!!editTrade}
        onClose={closeEdit}
        title={editTrade ? `Edit ${get(editTrade, 'defaultName', 'Trade Type')}` : ''}
        maxWidth="sm"
        fullWidth
        dialogActions={{ sx: { px: 3, pb: 2 } }}
        buttons={[
          { title: 'Cancel', variant: 'text', color: 'secondary', disabled: saving, onClick: closeEdit },
          { title: 'Submit', variant: 'contained', color: 'secondary', disabled: saving, onClick: submitEdit },
        ]}
      >
        {editTrade && (
          <div>
            <TextField
              label="Custom Name"
              size="small"
              variant="outlined"
              fullWidth
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Key ID"
              size="small"
              variant="outlined"
              fullWidth
              value={draft.clientKey}
              onChange={(e) => setDraft((d) => ({ ...d, clientKey: e.target.value }))}
            />
          </div>
        )}
      </Modal>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
};
